'use client';

import type { ViewMode } from '@/components/properties/ViewSwitcher';
import { PropertyListing } from '../../../../src/components/properties/PropertyListing';
import type { Property } from '../../../../src/lib/api/clients/properties';
import { AllysPropertyListView } from './PropertyListView';

export interface AllysPropertyListingProps {
  organisationId: string;
  locale?: 'en' | 'es' | 'fr';
  messages?: Record<string, unknown>;
  initialView?: ViewMode;
  showMapView?: boolean;
  /**
   * Optional properties provided by the server so we can render instantly
   */
  initialProperties?: Property[];
  /**
   * Fallback properties when the API is unavailable
   */
  fallbackProperties?: Property[];
  /**
   * Base path for property links (e.g., '/site/org-123' for tenant sites)
   * If not provided, defaults to '/{locale}'
   */
  basePath?: string;
  /**
   * Pre-selected property type ID to filter by (resolved from URL slug)
   */
  initialPropertyTypeId?: string | null;
}

/**
 * AllysPropertyListing
 * Theme-friendly wrapper around the headless PropertyListing component.
 * Handles data fetching while reusing the existing Allys list view styling.
 */
export function AllysPropertyListing({
  organisationId,
  locale = 'en',
  messages,
  initialView = 'grid',
  showMapView = false,
  initialProperties,
  fallbackProperties,
  basePath,
  initialPropertyTypeId = null,
}: AllysPropertyListingProps) {
  // Default basePath to /{locale} if not provided
  const effectiveBasePath = basePath || `/${locale}`;
  
  // When the server provides initialProperties, skip client-side refetch
  // The server already fetched fresh data with proper authentication
  const hasServerData = initialProperties && initialProperties.length > 0;
  
  return (
    <PropertyListing
      organisationId={organisationId}
      locale={locale}
      includeRelations
      initialView={initialView}
      refreshOnMount={!hasServerData}
      {...(initialProperties !== undefined && { initialProperties })}
      {...(fallbackProperties !== undefined && { fallbackProperties: fallbackProperties || initialProperties })}
      render={({ properties, isLoading, error, refetch }) => {
        if (isLoading && properties.length === 0) {
          return (
            <div className="mx-auto max-w-4xl rounded-xl border border-gray-200 bg-white px-6 py-5 text-center text-gray-700 shadow-sm">
              Loading properties…
            </div>
          );
        }

        if (error && properties.length === 0) {
          return (
            <div className="mx-auto max-w-4xl rounded-xl border border-amber-200 bg-amber-50 px-6 py-5 text-center text-amber-800 shadow-sm">
              <p className="font-semibold">We couldn&apos;t load properties right now.</p>
              <p className="mt-1 text-sm text-amber-700">{error}</p>
              <button
                type="button"
                onClick={() => refetch()}
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-md bg-[#243462] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1b294c] transition-colors"
              >
                Retry
              </button>
            </div>
          );
        }

        return (
          <AllysPropertyListView
            properties={properties}
            {...(messages !== undefined && { messages })}
            locale={locale}
            initialView={initialView}
            showMapView={showMapView}
            basePath={effectiveBasePath}
            initialPropertyTypeId={initialPropertyTypeId}
          />
        );
      }}
    />
  );
}

